import React, { useState } from 'react'
import OtpInput from 'react-otp-input'
import { Grid } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import {
  useNavigate,
  Link,
  useSearchParams,
  createSearchParams,
} from 'react-router-dom'
import useVerifyOtp from '../services/hooks/useVerifyOtp'
import useLocalStorage from '@/hooks/useLocalStorage'
import useForgotPassword from '../services/hooks/useForgotPassword'
import Popup from '@/components/Common/Popup'
import TopNav from '@/components/TopNav/TopNav'
import StyledButton from '@/components/Button/Button'
import Description from '@/components/Description/Description'
import StyledBgContainer from '@/components/StyledBgContainer/StyledBgContainer'
import Path from '@/router/localRoutes'
import './VerifyCode.css'
import { formatDuration } from '@/utils'

const VerifyCode = () => {
  const [otp, setOtp] = useState<string>('')
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const [isResent, setIsResent] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [seconds, setSeconds] = useState<number>(120)
  const [, setElapsedTime] = useState<number>(0)
  const [token, setToken] = useState<string>('')
  const [searchParams] = useSearchParams()
  const [role] = useLocalStorage('role')
  const email = searchParams.get('email')
  const navigate = useNavigate()

  const { mutate: forgotPassword, isError: isResendingError } =
    useForgotPassword()
  const {
    mutate: verifyCode,
    isPending: isVerifying,
    isError: isVerifyingError,
  } = useVerifyOtp()

  if (!email) {
    navigate(Path.ForgotPassword)
  }

  const handleResend = async () => {
    setIsResent(true)
    setSeconds(120)
    setElapsedTime(0)
    const payload = {
      email,
      role: role,
    }

    forgotPassword(payload, {
      onSuccess: () => {
        if (payload.email) {
          navigate({
            pathname: `${Path.VerifyCode}`,
            search: createSearchParams({
              email: payload.email,
            }).toString(),
          })
        }

        setSeconds(120)
      },
      onError: (error) => {
        setErrorMsg(error.message)
      },
    })

    const intervalId = setInterval(() => {
      setSeconds((prevSeconds) => {
        if (prevSeconds <= 1) {
          clearInterval(intervalId)
          return 0
        }
        return prevSeconds - 1
      })
      setElapsedTime((prevElapsedTime) => {
        if (prevElapsedTime >= 29) {
          clearInterval(intervalId)
          setIsResent(false)
        }
        return prevElapsedTime + 1
      })
    }, 1000)
  }

  const handleCodeVerification = async () => {
    const payload = {
      email: email,
      otp: otp,
    }

    verifyCode(payload, {
      onSuccess: (response) => {
        setToken(response?.data?.token)
        setShowPopup(true)
      },
      onError: (error) => {
        setErrorMsg(error.message)
      },
    })
  }

  const handleContinue = () => {
    navigate({
      pathname: `${Path.ChangePassword}`,
      search: createSearchParams({
        token: token,
      }).toString(),
    })
  }

  return (
    <div className="relative">
      <div
        className={`${showPopup ? 'blur-sm' : ''} transition-all duration-300`}
      >
        <StyledBgContainer>
          <Grid
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              color: '#7D7D7D',
            }}
          >
            <TopNav showArrow={true} />
            <h1 className="heading">Verify Code</h1>
            <Description>We sent required otp to your email</Description>
            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={4}
              inputStyle={{
                margin: '20px 10px',
                borderColor: '#365f91',
                border: '1.2px solid transparent',
                height: '12vh',
                width: '5vw',
                background:
                  'linear-gradient(black, black) padding-box, linear-gradient(180deg, #365f91 30%, #365f9133 100%) border-box',
                borderRadius: '21px',
                color: 'white',
              }}
              renderInput={(props) => <input {...props} />}
            />
            {isResendingError || isVerifyingError ? (
              <p className="error-msg">{errorMsg}</p>
            ) : null}
            {isResent ? (
              <Link
                className="mb-[20vh] mt-[17px] no-underline text-[#7d7d7d]"
                to={''}
              >
                Resend Code in{' '}
                <span className="text-white">{formatDuration(seconds)}</span>
              </Link>
            ) : (
              <div className="mb-[20vh] mt-1">
                <p>Didn&apos;t Receive Code ?</p>
                <a onClick={handleResend} className="link-style cursor-pointer">
                  Resend
                </a>
              </div>
            )}
            <div className="absolute bottom-16 left-0 right-0 flex flex-col items-center gap-14">
              {isVerifying ? (
                <StyledButton className="w-[130px] !h-[45px]">
                  <CircularProgress className="!text-[#181a21] !text-2xl" />
                </StyledButton>
              ) : (
                <StyledButton
                  onClick={handleCodeVerification}
                  disabled={!otp}
                  className="!shadow-2xl !font-sans w-[130px] !h-[45px] !text-[14px]"
                >
                  Verify Code
                </StyledButton>
              )}
              <p>
                Back to{' '}
                <Link className="link-style" to={Path.Signin}>
                  Sign In
                </Link>
              </p>
            </div>
          </Grid>
        </StyledBgContainer>
      </div>
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Popup
            title="Code Verified Successfully"
            message="Your code has been validated. Thank you for your confirmation!"
            buttonText="Continue"
            onButtonClick={handleContinue}
          />
        </div>
      )}
    </div>
  )
}

export default VerifyCode
